import React, {FC} from 'react';
import styled from 'styled-components';

import {
  buildHoverParentClassName,
  hoverSelector,
} from '../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {useDraggable} from '../../../../libs/shared/ui/src/hooks/dndHooks';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsWidgetsPageWidgetModel} from '../../../models/api/analyticsWidgetsPageModel';
import {getIsSidePanelOpen} from '../../../store/panels/analytics/details/analyticsPanelDetailsSelectors';
import {getAnalyticsAllViews} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {renderSupportReportMetricView, renderSupportReportType} from './supportReportConfigurationHelpers';

/*
 * Constants.
 */

const supportReportMetricCard = 'SUPPORT_REPORT_METRIC_CARD';

/*
 * Props.
 */

interface SupportReportConfigurationMetricProps {
  widget: AnalyticsWidgetsPageWidgetModel;
  index: number;
  moveWidget: (fromIndex: number, toIndex: number) => void;
  onDelete: () => void;
  onEdit: () => void;
}

/*
 * Style.
 */

const StyledMetricWrapperDiv = styled.div`
  position: relative;
  display: flex;
  flex-flow: row;
  gap: 8px;
  transform: translate(0, 0);
  border: 1px solid ${(p) => p.theme.greys.shade40};
  background: ${(p) => p.theme.greys.white};
  border-radius: 8px;
`;

const StyledDragHandleWrapperDiv = styled.div`
  display: grid;
  align-items: center;
  padding-left: 4px;
`;

const StyledIcon = styled(Icon)`
  cursor: grab;
`;

const StyledMetricDetailsWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-flow: column;
  gap: 8px;
  padding: 16px 16px 16px 0;
`;

const StyledTitleDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};
  line-height: 21px;
`;

const StyledDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
`;

const StyledDescriptionDiv = styled.div`
  line-height: 20px;
`;

const StyledActionsWrapperDiv = styled.div`
  display: none;
  position: absolute;
  top: 8px;
  right: 8px;
  flex-flow: row;
  background: ${(p) => p.theme.greys.white};
  padding: 4px;
  border-radius: 8px;
  gap: 4px;

  ${hoverSelector} {
    display: flex;
  }
`;

/*
 * Component.
 */

export const SupportReportConfigurationMetric: FC<SupportReportConfigurationMetricProps> = ({
  widget,
  index,
  moveWidget,
  onDelete,
  onEdit,
}) => {
  const theme = useUserTheme();
  const {views} = useAppSelector(getAnalyticsAllViews);
  const selectedView = views.find((view) => view.id === widget.viewId);
  const isSidePanelOpen = useAppSelector(getIsSidePanelOpen);

  const {elementRef, handlerId, handleRef} = useDraggable<HTMLDivElement>(
    supportReportMetricCard,
    `${index}`,
    index,
    moveWidget,
  );

  return (
    <StyledMetricWrapperDiv
      className={buildHoverParentClassName()}
      ref={elementRef}
      data-handler-id={handlerId}
    >
      <StyledDragHandleWrapperDiv ref={handleRef}>
        <StyledIcon name="grabHandleSmall" color={theme.greys.shade60} />
      </StyledDragHandleWrapperDiv>
      <StyledMetricDetailsWrapperDiv>
        <StyledTitleDiv>{widget.title}</StyledTitleDiv>
        <StyledDetailsWrapperDiv>
          <span>{renderSupportReportType(widget.type)}</span>
          <span>•</span>
          <span>{renderSupportReportMetricView(selectedView)}</span>
        </StyledDetailsWrapperDiv>
        {widget.description && <StyledDescriptionDiv>{widget.description}</StyledDescriptionDiv>}
      </StyledMetricDetailsWrapperDiv>
      {!isSidePanelOpen && (
        <StyledActionsWrapperDiv>
          <IconButton
            iconName="editSmall"
            label={<Trans id="analytics-automation-reports-support-report-metric-edit-button">Edit</Trans>}
            onClick={onEdit}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
          <IconButton
            iconName="trashSmall"
            label={
              <Trans id="analytics-automation-reports-support-report-metric-delete-button">Delete</Trans>
            }
            onClick={onDelete}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
        </StyledActionsWrapperDiv>
      )}
    </StyledMetricWrapperDiv>
  );
};
